import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Typography,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  Slider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../api/axios';

const DEFAULT_THUMBNAIL_THEME = {
  style: 'minimalist',
  color_scheme: 'soft_blues',
  icon_count: 2,
  background: 'clean_white',
  include_text: false,
  accent_color: '#5B8FC7',
  background_color: '#FFFFFF',
  thumbnail_text: ''
};

function normalizeHex6(value) {
  if (typeof value !== 'string') return null;
  let s = value.trim();
  if (s.startsWith('#')) s = s.slice(1);
  if (s.length === 3) {
    s = s.split('').map((c) => c + c).join('');
  }
  if (!/^[0-9A-Fa-f]{6}$/.test(s)) return null;
  return `#${s.toUpperCase()}`;
}

export default function ModuleSettingsModal({ 
  open, 
  onClose, 
  moduleCode, 
  moduleTitle, 
  qualificationId 
}) {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Settings state
  const [thumbnailTheme, setThumbnailTheme] = useState(() => ({ ...DEFAULT_THUMBNAIL_THEME }));
  
  const [assistantSettings, setAssistantSettings] = useState({
    role: 'curriculum_education_specialist',
    tone: 'professional',
    expertise_level: 'intermediate',
    focus_areas: ['core_concepts', 'applied_skills', 'professional_practice']
  });
  
  const [referenceMaterials, setReferenceMaterials] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadCategory, setUploadCategory] = useState('general');

  // Load settings when modal opens
  useEffect(() => {
    if (open && moduleCode && qualificationId) {
      loadSettings();
    }
  }, [open, moduleCode, qualificationId]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(`/qualifications/${qualificationId}/module-settings/${moduleCode}`);
      
      if (response.data?.success) {
        const settings = response.data.settings;
        setThumbnailTheme({
          ...DEFAULT_THUMBNAIL_THEME,
          ...(settings.thumbnail_theme || {})
        });
        setAssistantSettings(settings.assistant_settings || {
          role: 'curriculum_education_specialist',
          tone: 'professional',
          expertise_level: 'intermediate',
          focus_areas: ['core_concepts', 'applied_skills', 'professional_practice']
        });
        setReferenceMaterials(settings.reference_materials || []);
      }
    } catch (err) {

      setError('Failed to load settings: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      await api.put(`/qualifications/${qualificationId}/module-settings/${moduleCode}`, {
        thumbnail_theme: thumbnailTheme,
        assistant_settings: assistantSettings
      });
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {

      setError('Failed to save settings: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async () => {
    if (!uploadFile) return;
    
    try {
      setUploading(true);
      setError(null);
      
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('description', uploadDescription);
      formData.append('category', uploadCategory);
      
      const response = await api.post(
        `/qualifications/${qualificationId}/module-settings/${moduleCode}/reference-materials`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data?.success) {
        setReferenceMaterials(prev => [...prev, response.data.material]);
        setUploadFile(null);
        setUploadDescription('');
        setUploadCategory('general');
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (err) {

      setError('Failed to upload file: ' + (err.response?.data?.message || err.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteMaterial = async (materialId) => {
    try {
      await api.delete(`/qualifications/${qualificationId}/module-settings/${moduleCode}/reference-materials/${materialId}`);
      setReferenceMaterials(prev => prev.filter(m => m.id !== materialId));
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {

      setError('Failed to delete material: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDownloadMaterial = async (materialId) => {
    try {
      const response = await api.get(
        `/qualifications/${qualificationId}/module-settings/${moduleCode}/reference-materials/${materialId}/download`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = referenceMaterials.find(m => m.id === materialId)?.originalName || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {

      setError('Failed to download material: ' + (err.response?.data?.message || err.message));
    }
  };

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Module Settings: {moduleTitle}</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab label="Thumbnail Theme" />
            <Tab label="Assistant Settings" />
            <Tab label="Reference Materials" />
          </Tabs>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ m: 2 }}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ m: 2 }}>
                Settings saved successfully!
              </Alert>
            )}
            
            {/* Thumbnail Theme Tab */}
            <TabPanel value={activeTab} index={0}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Style</InputLabel>
                    <Select
                      value={thumbnailTheme.style}
                      onChange={(e) => setThumbnailTheme(prev => ({ ...prev, style: e.target.value }))}
                    >
                      <MenuItem value="minimalist">Minimalist</MenuItem>
                      <MenuItem value="modern">Modern</MenuItem>
                      <MenuItem value="classic">Classic</MenuItem>
                      <MenuItem value="creative">Creative</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Color Scheme</InputLabel>
                    <Select
                      value={thumbnailTheme.color_scheme}
                      onChange={(e) => setThumbnailTheme(prev => ({ ...prev, color_scheme: e.target.value }))}
                    >
                      <MenuItem value="soft_blues">Soft Blues</MenuItem>
                      <MenuItem value="warm_greens">Warm Greens</MenuItem>
                      <MenuItem value="neutral_grays">Neutral Grays</MenuItem>
                      <MenuItem value="banking_blue">Banking Blue</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid size={{ xs: 12 }}>
                  <Typography gutterBottom>Icon Count</Typography>
                  <Slider
                    value={thumbnailTheme.icon_count}
                    onChange={(e, value) => setThumbnailTheme(prev => ({ ...prev, icon_count: value }))}
                    min={1}
                    max={5}
                    step={1}
                    marks
                    valueLabelDisplay="auto"
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Background</InputLabel>
                    <Select
                      value={thumbnailTheme.background}
                      onChange={(e) => setThumbnailTheme(prev => ({ ...prev, background: e.target.value }))}
                    >
                      <MenuItem value="clean_white">Clean White</MenuItem>
                      <MenuItem value="light_blue">Light Blue</MenuItem>
                      <MenuItem value="gradient">Gradient</MenuItem>
                      <MenuItem value="pattern">Pattern</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={thumbnailTheme.include_text}
                        onChange={(e) => setThumbnailTheme(prev => ({ ...prev, include_text: e.target.checked }))}
                      />
                    }
                    label="Include Text"
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Accent (scheme) color
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Box
                      component="input"
                      type="color"
                      value={normalizeHex6(thumbnailTheme.accent_color) || DEFAULT_THUMBNAIL_THEME.accent_color}
                      onChange={(e) =>
                        setThumbnailTheme((prev) => ({ ...prev, accent_color: e.target.value.toUpperCase() }))
                      }
                      sx={{ width: 48, height: 40, p: 0, border: '1px solid', borderColor: 'divider', borderRadius: 1, cursor: 'pointer' }}
                    />
                    <TextField
                      size="small"
                      label="Hex"
                      placeholder="#5B8FC7"
                      value={thumbnailTheme.accent_color || ''}
                      onChange={(e) => setThumbnailTheme((prev) => ({ ...prev, accent_color: e.target.value }))}
                      onBlur={() => {
                        const n = normalizeHex6(thumbnailTheme.accent_color);
                        if (n) setThumbnailTheme((prev) => ({ ...prev, accent_color: n }));
                      }}
                      sx={{ minWidth: 120 }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Used in the generated thumbnail palette; presets apply if left invalid.
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Background color
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Box
                      component="input"
                      type="color"
                      value={normalizeHex6(thumbnailTheme.background_color) || DEFAULT_THUMBNAIL_THEME.background_color}
                      onChange={(e) =>
                        setThumbnailTheme((prev) => ({ ...prev, background_color: e.target.value.toUpperCase() }))
                      }
                      sx={{ width: 48, height: 40, p: 0, border: '1px solid', borderColor: 'divider', borderRadius: 1, cursor: 'pointer' }}
                    />
                    <TextField
                      size="small"
                      label="Hex"
                      placeholder="#FFFFFF"
                      value={thumbnailTheme.background_color || ''}
                      onChange={(e) => setThumbnailTheme((prev) => ({ ...prev, background_color: e.target.value }))}
                      onBlur={() => {
                        const n = normalizeHex6(thumbnailTheme.background_color);
                        if (n) setThumbnailTheme((prev) => ({ ...prev, background_color: n }));
                      }}
                      sx={{ minWidth: 120 }}
                    />
                  </Box>
                </Grid>

                {thumbnailTheme.include_text && (
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Thumbnail text"
                      value={thumbnailTheme.thumbnail_text || ''}
                      onChange={(e) =>
                        setThumbnailTheme((prev) => ({ ...prev, thumbnail_text: e.target.value.slice(0, 120) }))
                      }
                      helperText="Short text to render on the image (max 120 characters). If empty, the task title may be used."
                      multiline
                      rows={2}
                    />
                  </Grid>
                )}
              </Grid>
            </TabPanel>
            
            {/* Assistant Settings Tab */}
            <TabPanel value={activeTab} index={1}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Role"
                    value={assistantSettings.role}
                    onChange={(e) => setAssistantSettings(prev => ({ ...prev, role: e.target.value }))}
                    helperText="Define the AI assistant's role and expertise area"
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Tone</InputLabel>
                    <Select
                      value={assistantSettings.tone}
                      onChange={(e) => setAssistantSettings(prev => ({ ...prev, tone: e.target.value }))}
                    >
                      <MenuItem value="professional">Professional</MenuItem>
                      <MenuItem value="friendly">Friendly</MenuItem>
                      <MenuItem value="academic">Academic</MenuItem>
                      <MenuItem value="conversational">Conversational</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Expertise Level</InputLabel>
                    <Select
                      value={assistantSettings.expertise_level}
                      onChange={(e) => setAssistantSettings(prev => ({ ...prev, expertise_level: e.target.value }))}
                    >
                      <MenuItem value="beginner">Beginner</MenuItem>
                      <MenuItem value="intermediate">Intermediate</MenuItem>
                      <MenuItem value="advanced">Advanced</MenuItem>
                      <MenuItem value="expert">Expert</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid size={{ xs: 12 }}>
                  <Typography gutterBottom>Focus Areas</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {['core_concepts', 'applied_skills', 'professional_practice', 'compliance', 'risk_management', 'assessment_readiness'].map((area) => (
                      <Chip
                        key={area}
                        label={area.replace('_', ' ')}
                        onClick={() => {
                          const currentAreas = assistantSettings.focus_areas || [];
                          if (currentAreas.includes(area)) {
                            setAssistantSettings(prev => ({
                              ...prev,
                              focus_areas: currentAreas.filter(a => a !== area)
                            }));
                          } else {
                            setAssistantSettings(prev => ({
                              ...prev,
                              focus_areas: [...currentAreas, area]
                            }));
                          }
                        }}
                        color={assistantSettings.focus_areas?.includes(area) ? 'primary' : 'default'}
                        variant={assistantSettings.focus_areas?.includes(area) ? 'filled' : 'outlined'}
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </TabPanel>
            
            {/* Reference Materials Tab */}
            <TabPanel value={activeTab} index={2}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Upload Reference Material</Typography>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12 }}>
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx,.txt,.md"
                            onChange={(e) => setUploadFile(e.target.files[0])}
                            style={{ display: 'none' }}
                            id="file-upload"
                          />
                          <label htmlFor="file-upload">
                            <Button
                              variant="outlined"
                              component="span"
                              startIcon={<UploadIcon />}
                              fullWidth
                            >
                              Choose File
                            </Button>
                          </label>
                          {uploadFile && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              Selected: {uploadFile.name}
                            </Typography>
                          )}
                        </Grid>
                        
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            label="Description"
                            value={uploadDescription}
                            onChange={(e) => setUploadDescription(e.target.value)}
                            placeholder="Brief description of the material"
                          />
                        </Grid>
                        
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <FormControl fullWidth>
                            <InputLabel>Category</InputLabel>
                            <Select
                              value={uploadCategory}
                              onChange={(e) => setUploadCategory(e.target.value)}
                            >
                              <MenuItem value="general">General</MenuItem>
                              <MenuItem value="policy">Policy</MenuItem>
                              <MenuItem value="procedure">Procedure</MenuItem>
                              <MenuItem value="reference">Reference</MenuItem>
                              <MenuItem value="example">Example</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        
                        <Grid size={{ xs: 12 }}>
                          <Button
                            variant="contained"
                            onClick={handleFileUpload}
                            disabled={!uploadFile || uploading}
                            startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
                            fullWidth
                          >
                            {uploading ? 'Uploading...' : 'Upload'}
                          </Button>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid size={{ xs: 12 }}>
                  <Typography variant="h6" gutterBottom>Reference Materials</Typography>
                  <List>
                    {referenceMaterials.map((material) => (
                      <ListItem key={material.id} divider>
                        <ListItemText
                          primary={material.originalName}
                          secondary={`${material.description} • ${material.category} • ${new Date(material.uploadedAt).toLocaleDateString()}`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            onClick={() => handleDownloadMaterial(material.id)}
                            size="small"
                          >
                            <DownloadIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteMaterial(material.id)}
                            size="small"
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                    {referenceMaterials.length === 0 && (
                      <ListItem>
                        <ListItemText primary="No reference materials uploaded yet" />
                      </ListItem>
                    )}
                  </List>
                </Grid>
              </Grid>
            </TabPanel>
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
        <Button 
          onClick={handleSave} 
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />} 
          color="primary"
          disabled={saving || loading}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
